import { MultiMetricOptionMixin } from '../../mixin';
import metricTranslatorMixin from '@/mixins/translator/metric';

import QuickControlDropdown from '../Dropdown/index';
import { useFieldGuard } from '../useFieldGuard';
import { FIELD_KEY, makeMetricFormat } from '../../utils';

import ColumnChartIcon from '@svgIcons/chart_column.svg';
import LineChartIcon from '@svgIcons/chart_line.svg';
import AlignLeftIcon from '@svgIcons/align_left.svg';
import AlignRightIcon from '@svgIcons/align_right.svg';

import * as S from '../styles';
import * as M from './styles';

import { IFRAME_EVENT_MESSAGE, CHART_TYPES } from '@consts';
import { MAX_QUICK_CONTROL_METRIC_COUNT } from '../../consts';

const AXIS_POSITION = {
    LEFT: 'left',
    RIGHT: 'right',
};

export default {
    name: 'ComboMetricQuickControl',
    mixins: [MultiMetricOptionMixin, metricTranslatorMixin],
    setup(props, { emit }) {
        const mixinSetup = MultiMetricOptionMixin.setup(props);
        useFieldGuard({
            getSelectedId: () => props.selectedField?.[FIELD_KEY] ?? '',
            getEditableIds: () => props.exposureList,
            getFixedIds: () => props.fixedList,
            getDefaultIds: () => props.defaultList,
            emitUpdateList: (newList) =>
                emit('update:exposureListMap', {
                    key: props.exposureListKey,
                    value: newList,
                }),
            emitUpdateSelected: (newSelected) => {
                const {
                    type = CHART_TYPES.BAR,
                    axisPosition = AXIS_POSITION.LEFT,
                } = props.selectedField ?? {};
                emit(
                    'update:exposureSelectedFieldMap',
                    newSelected
                        ? { [FIELD_KEY]: newSelected, type, axisPosition }
                        : { type, axisPosition }
                );
            },
        });

        return mixinSetup;
    },
    props: {
        panelKey: String,
        optionTitle: String,
        hide: { type: Boolean, default: false },
        /**
         * 선택된 combo metric 필드 (Object: { key, type, axisPosition })
         */
        selectedField: {
            type: Object,
            default() {
                return {};
            },
        },
        /**
         * exposureListMap 에서 사용할 key
         */
        exposureListKey: { type: String, required: true },
        /**
         * 선택된 metric key 목록 (string[] | undefined)
         * undefined = 미설정 상태 (default list 사용 중)
         */
        exposureList: { type: Array, default: undefined },
        /**
         * exposureList가 undefined일 때 표시할 기본 목록 (string[])
         */
        defaultList: { type: Array, default: () => [] },
        /**
         * 드롭다운에 항상 고정 표시할 옵션 목록 (string[])
         */
        fixedList: { type: Array, default: () => [] },
        canEditCustomMetric: { type: Boolean, default: true },
    },
    data() {
        return {
            iframeListeners_: [
                {
                    eventType: IFRAME_EVENT_MESSAGE.UPDATE_METRIC_PANEL,
                    listener: (e) => {
                        const { data } = e;
                        if (!data || data.panelKey !== this.panelKey) return;

                        const { selectedFields } = data;

                        this.$emit('update:exposureListMap', {
                            key: this.exposureListKey,
                            value: selectedFields,
                        });
                    },
                },
            ],
        };
    },
    computed: {
        title() {
            return this.$t(
                'FIELD_PANEL.metric_exposure_setting.second_panel_title',
                { option: this.optionTitle }
            );
        },
        mainGuide() {
            return this.$t(
                'FIELD_PANEL.metric_exposure_setting.second_panel_main_guide'
            );
        },
        subGuide() {
            return this.$t(
                'FIELD_PANEL.metric_exposure_setting.second_panel_sub_guide'
            );
        },
        /**
         * Override mixin: openReactPanel/syncSelectedFields 에서 사용
         * undefined(미설정) 상태에서는 defaultList를 현재 선택 목록으로 간주
         */
        selectedFields() {
            return this.exposureList ?? this.defaultList;
        },
        limitCount() {
            return MAX_QUICK_CONTROL_METRIC_COUNT;
        },
    },
    methods: {
        handleListUpdate(newList) {
            this.$emit('update:exposureListMap', {
                key: this.exposureListKey,
                value: newList,
            });
        },
        handleRemoveItem(key) {
            const updated = (this.exposureList ?? this.defaultList).filter((v) => v !== key);

            this.$emit('update:exposureListMap', {
                key: this.exposureListKey,
                value: updated,
            });

            if ((this.selectedField?.[FIELD_KEY] ?? '') === key) {
                const {
                    type = CHART_TYPES.BAR,
                    axisPosition = AXIS_POSITION.LEFT,
                } = this.selectedField ?? {};
                this.$emit(
                    'update:exposureSelectedFieldMap',
                    updated[0]
                        ? { [FIELD_KEY]: updated[0], type, axisPosition }
                        : { type, axisPosition }
                );
            }
        },
        handleSelectItem(key) {
            const {
                type = CHART_TYPES.BAR,
                axisPosition = AXIS_POSITION.LEFT,
            } = this.selectedField ?? {};
            this.$emit('update:exposureSelectedFieldMap', {
                [FIELD_KEY]: key,
                type,
                axisPosition,
            });
        },
        handleChartTypeUpdate(type) {
            this.$emit('update:exposureSelectedFieldMap', {
                ...this.selectedField,
                type,
            });
        },
        handleAxisPositionUpdate(axisPosition) {
            this.$emit('update:exposureSelectedFieldMap', {
                ...this.selectedField,
                axisPosition,
            });
        },
        renderItem() {
            const {
                exposureList,
                defaultList,
                fixedList,
                selectedField,
                getMetricName,
            } = this;
            const selectedKey = selectedField?.[FIELD_KEY] ?? '';

            return (
                <S.ExposureDropdownWrapper>
                    <QuickControlDropdown
                        fieldType="metric"
                        exposureList={exposureList ?? defaultList}
                        selectedValue={selectedKey}
                        translator={(key) => key === '' ? this.$t('NEW_DASHBOARD.none') : getMetricName(makeMetricFormat(key))}
                        fixedList={fixedList}
                        on={{
                            'update:list': this.handleListUpdate,
                            'remove:item': this.handleRemoveItem,
                            'select:item': this.handleSelectItem,
                        }}
                    />
                </S.ExposureDropdownWrapper>
            );
        },
        renderComboControls() {
            const {
                type = CHART_TYPES.BAR,
                axisPosition = AXIS_POSITION.LEFT,
            } = this.selectedField ?? {};

            return (
                <M.ComboControlsWrapper onClick={(e) => e.stopPropagation()}>
                    <M.SegmentGroupWrapper>
                        <M.SegmentGroup>
                            <M.SegmentLabel>
                                {this.$t('NEW_DASHBOARD.combo_chart_type')}
                            </M.SegmentLabel>
                            <M.SegmentControl>
                                <M.SegmentButton
                                    active={type === CHART_TYPES.BAR}
                                    onClick={() =>
                                        this.handleChartTypeUpdate(
                                            CHART_TYPES.BAR
                                        )
                                    }
                                >
                                    <ColumnChartIcon />
                                    <M.SegmentButtonLabel>
                                        Bar
                                    </M.SegmentButtonLabel>
                                </M.SegmentButton>
                                <M.SegmentButton
                                    active={type === CHART_TYPES.LINE}
                                    onClick={() =>
                                        this.handleChartTypeUpdate(
                                            CHART_TYPES.LINE
                                        )
                                    }
                                >
                                    <LineChartIcon />
                                    <M.SegmentButtonLabel>
                                        Line
                                    </M.SegmentButtonLabel>
                                </M.SegmentButton>
                            </M.SegmentControl>
                        </M.SegmentGroup>

                        <M.SegmentDivider />

                        <M.SegmentGroup>
                            <M.SegmentLabel>
                                {this.$t('NEW_DASHBOARD.combo_chart_axis')}
                            </M.SegmentLabel>
                            <M.SegmentControl>
                                <M.SegmentButton
                                    active={axisPosition === AXIS_POSITION.LEFT}
                                    onClick={() =>
                                        this.handleAxisPositionUpdate(
                                            AXIS_POSITION.LEFT
                                        )
                                    }
                                >
                                    <AlignLeftIcon />
                                    <M.SegmentButtonLabel>
                                        L
                                    </M.SegmentButtonLabel>
                                </M.SegmentButton>
                                <M.SegmentButton
                                    active={
                                        axisPosition === AXIS_POSITION.RIGHT
                                    }
                                    onClick={() =>
                                        this.handleAxisPositionUpdate(
                                            AXIS_POSITION.RIGHT
                                        )
                                    }
                                >
                                    <AlignRightIcon />
                                    <M.SegmentButtonLabel>
                                        R
                                    </M.SegmentButtonLabel>
                                </M.SegmentButton>
                            </M.SegmentControl>
                        </M.SegmentGroup>
                    </M.SegmentGroupWrapper>
                </M.ComboControlsWrapper>
            );
        },
    },
    render() {
        const { optionTitle, wrapOptions, renderItem, renderComboControls } =
            this;

        return (
            <S.ComponentWrapper hide={this.hide}>
                {wrapOptions({
                    headerContent: renderItem(),
                    children: renderComboControls(),
                    title: optionTitle,
                })}
            </S.ComponentWrapper>
        );
    },
};
